import React, { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Sparkles, RefreshCw, Calendar } from "lucide-react";

function StatusLine({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between border-b border-border px-4 py-3 last:border-0">
      <span className="font-mono text-xs uppercase text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        {ok === true && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
        {ok === false && <XCircle className="h-4 w-4 text-destructive" />}
        <span className="font-mono text-xs">{value}</span>
      </div>
    </div>
  );
}

export default function AdminSystem() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [sched, setSched] = useState(null);
  const [reloading, setReloading] = useState(false);

  const loadSched = () => {
    api.get("/admin/scheduler").then(({ data }) => setSched(data)).catch(() => setSched({ error: "Failed to load" }));
  };

  useEffect(() => {
    api.get("/admin/system").then(({ data }) => setD(data));
    loadSched();
  }, []);

  const reloadJobs = async () => {
    setReloading(true);
    try {
      const { data } = await api.post("/admin/scheduler/reload");
      toast.success(`Re-registered ${data.count} job(s)`);
      loadSched();
    } catch (e) {
      toast.error("Reload failed");
    } finally {
      setReloading(false);
    }
  };

  if (!d) return <div className="font-mono text-sm text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {t("admin.nav.system")}</div>
      <h1 className="mt-2 font-display text-2xl font-black tracking-tight">{t("admin.system.title")}</h1>

      <section className="mt-8 border border-border bg-card">
        <StatusLine
          label={t("admin.system.oauth")}
          ok={d.oauth_configured}
          value={d.oauth_configured ? t("admin.system.configured") : t("admin.system.notConfigured")}
        />
        <StatusLine
          label={t("admin.system.stripe")}
          ok={d.stripe_configured}
          value={d.stripe_configured ? t("admin.system.configured") : t("admin.system.notConfigured")}
        />
        <StatusLine
          label={t("admin.system.demoMode")}
          ok={d.demo_mode}
          value={d.demo_mode ? t("admin.system.enabled") : t("admin.system.disabled")}
        />
      </section>

      <section className="mt-6 border border-border bg-card">
        <StatusLine label={t("admin.system.adminEmails")} value={d.admin_emails.join(", ") || "—"} />
        <StatusLine label={t("admin.system.frontendUrl")} value={d.frontend_url || "—"} />
        <StatusLine label={t("admin.system.redirectUri")} value={d.redirect_uri || "—"} />
        <StatusLine label={t("admin.system.serverTime")} value={new Date(d.server_time).toLocaleString()} />
      </section>

      {!d.oauth_configured && (
        <div className="mt-6 border border-amber-500/40 bg-amber-500/10 p-4">
          <div className="flex items-center gap-2 font-mono text-xs uppercase text-amber-700 dark:text-amber-400">
            <Sparkles className="h-4 w-4" /> Next step
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Add <code className="font-mono">GOOGLE_CLIENT_ID</code> and{" "}
            <code className="font-mono">GOOGLE_CLIENT_SECRET</code> to{" "}
            <code className="font-mono">/app/backend/.env</code>, then restart the backend to enable real Google Drive
            connections. Authorized redirect URI:
            <code className="ml-1 font-mono">{d.redirect_uri}</code>.
          </p>
        </div>
      )}

      {/* Scheduler diagnostics */}
      <section className="mt-8" data-testid="admin-scheduler-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-[#002FA7]">
            <Calendar className="h-4 w-4" /> Scheduler
          </div>
          <button
            type="button"
            onClick={reloadJobs}
            disabled={reloading}
            data-testid="admin-scheduler-reload-btn"
            className="flex items-center gap-2 border border-border bg-card px-3 py-1.5 font-mono text-xs uppercase tracking-wider hover:border-[#002FA7] disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${reloading ? "animate-spin" : ""}`} />
            Re-register all jobs
          </button>
        </div>

        {sched === null ? (
          <div className="mt-3 font-mono text-sm text-muted-foreground">{t("common.loading")}</div>
        ) : sched.error ? (
          <div className="mt-3 border border-destructive/40 bg-destructive/10 p-4 font-mono text-xs text-destructive">{sched.error}</div>
        ) : (
          <div className="mt-3 border border-border bg-card">
            <StatusLine label="Started" ok={!!sched.started} value={String(sched.started)} />
            <StatusLine label="APScheduler jobs" value={String((sched.aps_jobs || []).length)} />
            <StatusLine label="DB jobs (enabled, non-manual)" value={String((sched.db_jobs || []).length)} />
            {(sched.db_jobs || []).map((j) => (
              <div key={j.id} className="border-t border-border px-4 py-3" data-testid={`admin-scheduler-job-${j.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs">{j.name || j.id}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {j.frequency} · registered={String(j.registered_in_scheduler)}
                  </span>
                </div>
                <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                  next: <span className="text-foreground">{j.next_run_at ? new Date(j.next_run_at).toLocaleString() : "—"}</span>
                  {" · "}last: <span className="text-foreground">{j.last_run_at ? new Date(j.last_run_at).toLocaleString() : "—"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
